// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useState } from 'react';

import type { ProviderAccessPageContext, ProviderRejectionRequest } from '../types/providerAccess';
import type { WizardStep } from '../types/wizard';
import { WizardLayout } from './WizardLayout';
import { WizardHeader } from './WizardHeader';
import { WizardSidebar } from './WizardSidebar';
import { WizardStepContent } from './WizardStepContent';
import { RejectionPanel } from './wizard/RejectionPanel';

export interface ProviderNewBusinessWizardProps {
  context: ProviderAccessPageContext;
  submitting: boolean;
  onIssue: (policyNumber: string) => Promise<boolean>;
  onReject: (request: ProviderRejectionRequest) => Promise<boolean>;
}

const NB_STEPS: WizardStep[] = [
  { id: 'loa-validation', name: 'New Business Application', index: 0, status: 'incomplete', savedAt: null },
];

/**
 * Single-step New Business Application form for the provider Open Access page.
 *
 * Renders when {@code context.documentType === 'NEW_BUSINESS'}. The provider
 * reviews the application (resolved provider / product / applicant; the signed
 * application PDF is in the email) and either ISSUES a policy — returning the
 * issued policy number — or REJECTS with a reason. Reuses the same WizardLayout
 * shell + RejectionPanel as the LOA/CoA wizards.
 */
export function ProviderNewBusinessWizard({
  context,
  submitting,
  onIssue,
  onReject,
}: ProviderNewBusinessWizardProps) {
  const [issuing, setIssuing] = useState<boolean | undefined>(undefined);
  const [policyNumber, setPolicyNumber] = useState('');
  const [policyNumberError, setPolicyNumberError] = useState<string | null>(null);

  const title = context.clientName
    ? `New Business Application for ${context.clientName}`
    : 'New Business Application';

  const description = context.brokerageName
    ? `${context.brokerageName} has submitted a new business application for your review. You can view the signed application form in the email sent to you by ${context.brokerageName}. Issue the policy by returning its policy number, or reject the application with a reason.`
    : 'Please review the new business application sent to you via email, then issue the policy or reject the application.';

  const handleIssue = useCallback(async () => {
    if (!policyNumber.trim()) {
      setPolicyNumberError('A policy number is required to issue the policy.');
      return;
    }
    setPolicyNumberError(null);
    await onIssue(policyNumber.trim());
  }, [policyNumber, onIssue]);

  const summaryRow = (label: string, value?: string | null) =>
    value ? (
      <div className="flex gap-md">
        <div className="w-[140px] shrink-0 text-grey-02 font-sans text-body-02 font-normal leading-normal not-italic">
          {label}
        </div>
        <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">{value}</div>
      </div>
    ) : null;

  return (
    <WizardLayout context={context}>
      <WizardHeader context={context} title={title} lastSavedAt={null} />
      <WizardSidebar
        steps={NB_STEPS}
        currentStepId="loa-validation"
        onStepClick={() => {}}
        canNavigateTo={() => true}
      />
      <WizardStepContent>
        <div className="flex flex-col" data-testid="provider-new-business-wizard">
          <h2 className="text-black font-sans text-h5 font-normal leading-normal not-italic">
            New Business Application
          </h2>

          <div className="mt-sm text-black font-sans text-body-02 font-normal leading-normal not-italic">
            {description}
          </div>

          <div className="mt-lg flex flex-col gap-sm" data-testid="nb-application-summary">
            {summaryRow('Provider', context.providerName)}
            {summaryRow('Product', context.policyTypeName)}
            {summaryRow('Applicant', context.clientName)}
          </div>

          <div className="mt-lg text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Do you want to issue this policy?
          </div>

          <div className="mt-md flex flex-col gap-md">
            <label
              className="flex items-center gap-[12px] cursor-pointer"
              onClick={() => setIssuing(true)}
              data-testid="nb-issue-yes"
            >
              <div className="w-[16px] h-[16px] rounded-full border border-purple-primary flex items-center justify-center bg-white shrink-0">
                {issuing === true && <div className="w-[8px] h-[8px] rounded-full bg-purple-primary" />}
              </div>
              <div className="text-black font-sans text-body-02 font-normal leading-normal not-italic">
                Yes — issue the policy
              </div>
            </label>

            <label
              className="flex items-center gap-[12px] cursor-pointer"
              onClick={() => setIssuing(false)}
              data-testid="nb-issue-no"
            >
              <div className="w-[16px] h-[16px] rounded-full border border-purple-primary flex items-center justify-center bg-white shrink-0">
                {issuing === false && <div className="w-[8px] h-[8px] rounded-full bg-purple-primary" />}
              </div>
              <div className="text-black font-sans text-body-02 font-normal leading-normal not-italic">
                No — reject the application
              </div>
            </label>
          </div>

          {issuing === true && (
            <>
              <div className="mt-lg w-full h-[1px] bg-[#E1E1E1]" />

              <div className="mt-lg flex flex-col gap-sm">
                <label
                  htmlFor="nb-policy-number"
                  className="text-black font-sans text-body-02 font-medium leading-normal not-italic"
                >
                  Policy number
                </label>
                <input
                  id="nb-policy-number"
                  data-testid="nb-policy-number"
                  type="text"
                  value={policyNumber}
                  onChange={(e) => {
                    setPolicyNumber(e.target.value);
                    if (policyNumberError) setPolicyNumberError(null);
                  }}
                  placeholder="Enter the issued policy number"
                  className="h-[48px] w-full px-md rounded-input border border-[#E1E1E1] bg-white font-sans text-body-02 font-normal leading-normal not-italic text-black outline-none focus:border-purple-primary"
                />
                {policyNumberError && (
                  <div
                    className="text-error-text font-sans text-body-03 font-normal leading-normal not-italic"
                    data-testid="nb-policy-number-error"
                  >
                    {policyNumberError}
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-lg">
                <button
                  type="button"
                  disabled={submitting}
                  onClick={handleIssue}
                  data-testid="nb-issue-submit"
                  className="h-[48px] px-xl rounded-button bg-purple-primary border-none cursor-pointer transition-colors hover:bg-[#1E0649] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="text-white font-sans text-body-02 font-medium leading-normal not-italic capitalize">
                    {submitting ? 'Submitting...' : 'Issue Policy'}
                  </div>
                </button>
              </div>
            </>
          )}

          {issuing === false && (
            <RejectionPanel
              rejectionReasons={context.rejectionReasons ?? []}
              submitting={submitting}
              onReject={onReject}
              submitLabel="Reject Application"
            />
          )}
        </div>
      </WizardStepContent>
    </WizardLayout>
  );
}
